import { lessonMarkdownComponents } from '../lib/lesson-markdown';
/**
 * LessonPage - страница просмотра урока
 * apps/web/src/pages/LessonPage.tsx
 * 
 * Поддержка:
 * - YouTube/RuTube видео (автоопределение по ссылке)
 * - Адаптивные картинки
 * - Таблицы с горизонтальным скроллом
 * - Подсветка кода
 */

import { useEffect, useState, useRef } from 'react';
import { VideoEmbed, pickVideoUrl, isDirectVideoFile } from '../lib/video';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  getLesson,
  markLessonViewed,
  submitLessonFeedback,
  markCollectionLessonViewed,
  Lesson,
  LessonUserProgress,
} from '../lib/lessons-api';







/** Оценка времени урока: явное content.minutes, иначе ~150 слов в минуту + длительность видео/аудио. */
function lessonMinutes(lesson: any): number {
  const explicit = Number(lesson?.content?.minutes);
  if (explicit > 0) return Math.round(explicit);
  const words = String(lesson?.content?.text || '').split(/\s+/).filter(Boolean).length;
  const media = Number(lesson?.media?.duration || 0) / 60;
  return Math.max(1, Math.round(words / 150 + media));
}

export default function LessonPage() {
  const { id: collectionId, moduleId } = useParams<{
    id: string;
    moduleId: string;
  }>();
  const [searchParams] = useSearchParams();
  // Урок открывается по адресу /collections/<курс>/lesson/<модуль>: приложение добавляет
  // ?lessonId=…, а внешние ссылки (серверная страница курса, письма, посты) — нет,
  // и страница висела на «Загрузка урока…» (владелец, 07.09.2026). Без параметра
  // берём id урока из карточки курса по модулю.
  const lessonIdFromQuery = searchParams.get('lessonId');
  const [lessonId, setLessonId] = useState<string | null>(lessonIdFromQuery);
  useEffect(() => {
    if (lessonIdFromQuery) { setLessonId(lessonIdFromQuery); return; }
    if (!collectionId || !moduleId) return;
    let cancelled = false;
    fetch(`/api/collections/${collectionId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('курс не найден'))))
      .then((d) => {
        const mods = (d?.collection?.sections || []).flatMap((sec: any) => sec.modules || []);
        const m = mods.find((x: any) => x.id === moduleId);
        if (cancelled) return;
        if (m?.lesson?.id) setLessonId(m.lesson.id);
        else { setError('В этом модуле нет урока'); setLoading(false); }
      })
      .catch((e) => { if (!cancelled) { setError(e instanceof Error ? e.message : 'Ошибка загрузки курса'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [lessonIdFromQuery, collectionId, moduleId]);
  const navigate = useNavigate();

  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // У урока может быть несколько ссылок на один ролик (RuTube, ВК, YouTube).
  // Показываем ту, что зритель из России действительно откроет: YouTube там не грузится.
  const videoUrl = lesson
    ? pickVideoUrl([
        (lesson.media as any)?.rutubeUrl,
        (lesson.media as any)?.vkUrl,
        lesson.media?.videoUrl,
        (lesson.media as any)?.youtubeUrl,
      ])
    : null;
  const [_userProgress, setUserProgress] = useState<LessonUserProgress | null>(null);
  const [activeTab, setActiveTab] = useState<'text' | 'audio' | 'video'>('text');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [showKeyPoints, setShowKeyPoints] = useState(true);

  // Трекинг времени просмотра
  const startTimeRef = useRef<number>(Date.now());
  const hasMarkedViewedRef = useRef(false);

  // Загрузка урока
  useEffect(() => {
    if (!lessonId) return;

    const loadLesson = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getLesson(lessonId);
        setLesson(response.lesson);
        setUserProgress(response.userProgress || null);
        setActiveTab(response.lesson.type);

        // Сбрасываем таймер
        startTimeRef.current = Date.now();
        hasMarkedViewedRef.current = false;
      } catch (err) {
        console.error('Failed to load lesson:', err);
        setError(err instanceof Error ? err.message : 'Ошибка загрузки урока');
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [lessonId]);

  // Отметка просмотра при уходе со страницы
  useEffect(() => {
    const markViewed = async () => {
      if (!lessonId || hasMarkedViewedRef.current) return;

      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (timeSpent < 5) return; // Минимум 5 секунд

      hasMarkedViewedRef.current = true;

      try {
        await markLessonViewed(lessonId, timeSpent);
      } catch (err) {
        console.error('Failed to mark lesson viewed (lessons API):', err);
      }

      // Если это урок из коллекции — отмечаем в коллекции тоже
      if (collectionId && moduleId) {
        try {
          await markCollectionLessonViewed(collectionId, moduleId);
        } catch (err) {
          console.error('Failed to mark collection lesson viewed:', err);
        }
      }
    };

    // При размонтировании компонента
    return () => {
      markViewed();
    };
  }, [lessonId, collectionId, moduleId]);

  // Обработка feedback
  const handleFeedback = async (type: 'positive' | 'negative') => {
    if (!lessonId || feedbackSent) return;

    try {
      await submitLessonFeedback(lessonId, type);
      setFeedbackSent(true);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  // Переход к тесту
  const handleStartTest = async () => {
    if (!collectionId || !moduleId || !lessonId) return;
    console.log('handleStartTest called', { collectionId, moduleId, lessonId });

    // ВСЕГДА отмечаем урок как просмотренный при нажатии "Пройти тест"
    if (!hasMarkedViewedRef.current) {
      hasMarkedViewedRef.current = true;
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);

      // Первый вызов — lessons API (может упасть с 401 для guest)
      try {
        await markLessonViewed(lessonId, timeSpent);
        console.log('Lesson viewed marked (lessons API)');
      } catch (err) {
        console.error('Failed to mark lesson viewed (lessons API):', err);
      }

      // Второй вызов — collections API (должен работать)
      try {
        await markCollectionLessonViewed(collectionId, moduleId);
        console.log('Collection lesson-viewed marked successfully');
      } catch (err) {
        console.error('Failed to mark collection lesson viewed:', err);
      }
    }

    // Переходим к тесту
    navigate(`/collections/${collectionId}/play/${moduleId}`);
  };

  // Назад к курсу
  const handleBack = () => {
    if (collectionId) {
      navigate(`/collections/${collectionId}`);
    } else {
      navigate('/collections');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] text-[#252A2D] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0F766E] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#626B73]">Загрузка урока...</p>
        </div>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="min-h-screen bg-[#F7F7F4] text-[#252A2D] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">📚</div>
          <h1 className="text-xl font-bold mb-2">Урок не найден</h1>
          <p className="text-[#626B73] mb-4">{error || 'Урок не существует'}</p>
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-[#0F766E] rounded-lg hover:bg-[#0B5F59] transition"
          >
            Назад к курсу
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F4] text-[#252A2D]">
      {/* Header */}
      <header className="sticky top-0 bg-[#F7F7F4]/95 backdrop-blur border-b border-[#E1E5E3] z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-[#626B73] hover:text-[#252A2D] transition"
          >
            <span>←</span>
            <span className="hidden sm:inline">Назад к курсу</span>
          </button>
          <h1 className="text-lg font-semibold truncate max-w-[200px] sm:max-w-none">
            {lesson.title}
          </h1>
          <a href={`/support?from=${encodeURIComponent(window.location.href)}`} className="text-sm text-[#0F766E] hover:underline whitespace-nowrap" title="Задать вопрос по уроку">Задать вопрос</a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 overflow-x-hidden">
        {/* Format Switcher (если есть аудио/видео) */}
        {(lesson.media?.audioUrl || videoUrl) && (
          <div className="flex gap-2 mb-6 p-1 bg-white border border-[#E1E5E3] rounded-lg w-fit">
            <button
              onClick={() => setActiveTab('text')}
              className={`px-4 py-2 rounded-md transition ${
                activeTab === 'text'
                  ? 'bg-[#0F766E] text-[#252A2D]'
                  : 'text-[#626B73] hover:text-[#252A2D]'
              }`}
            >
              📝 Текст
            </button>
            {lesson.media?.audioUrl && (
              <button
                onClick={() => setActiveTab('audio')}
                className={`px-4 py-2 rounded-md transition ${
                  activeTab === 'audio'
                    ? 'bg-[#0F766E] text-[#252A2D]'
                    : 'text-[#626B73] hover:text-[#252A2D]'
                }`}
              >
                🎧 Аудио
              </button>
            )}
            {videoUrl && (
              <button
                onClick={() => setActiveTab('video')}
                className={`px-4 py-2 rounded-md transition ${
                  activeTab === 'video'
                    ? 'bg-[#0F766E] text-[#252A2D]'
                    : 'text-[#626B73] hover:text-[#252A2D]'
                }`}
              >
                🎬 Видео
              </button>
            )}
          </div>
        )}

        {/* Main Content */}
        {activeTab === 'text' && (<>
          {(lesson.content as any).goal && (
            <div className="mb-5 rounded-xl border border-[#E1E5E3] bg-[#E5F2EF] px-4 py-3 text-[#252A2D]">
              <span className="mr-2">🎯</span>{(lesson.content as any).goal}
              <span className="ml-2 text-sm text-[#626B73]">· ≈ {lessonMinutes(lesson)} мин</span>
            </div>
          )}
          <article className="prose prose-lg prose-headings:text-[#252A2D] prose-a:text-[#0F766E] prose-strong:text-[#252A2D] text-[#252A2D] max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={lessonMarkdownComponents()}
            >
              {lesson.content.text}
            </ReactMarkdown>
          </article>
        </>)}

        {activeTab === 'audio' && lesson.media?.audioUrl && (
          <div className="bg-white border border-[#E1E5E3] rounded-xl p-6">
            <audio controls className="w-full" src={lesson.media.audioUrl}>
              Ваш браузер не поддерживает аудио
            </audio>
            {lesson.media.duration && (
              <p className="text-[#626B73] text-sm mt-2">
                Длительность: {Math.round(lesson.media.duration / 60)} мин
              </p>
            )}
          </div>
        )}

        {activeTab === 'video' && videoUrl && (
          <div className="bg-white border border-[#E1E5E3] rounded-xl overflow-hidden p-2">
            {isDirectVideoFile(videoUrl) ? (
              <video controls className="w-full" src={videoUrl} poster={lesson.media?.thumbnailUrl}>
                Ваш браузер не поддерживает видео
              </video>
            ) : (
              // Ссылка на RuTube, ВК или YouTube — показываем через общий проигрыватель
              <VideoEmbed url={videoUrl} />
            )}
          </div>
        )}

        {/* Key Points */}
        {lesson.settings.showKeyPoints &&
          lesson.content.keyPoints &&
          lesson.content.keyPoints.length > 0 && (
            <div className="mt-8 bg-[#F0F4F2] border border-[#E1E5E3] rounded-xl p-4">
              <button
                onClick={() => setShowKeyPoints(!showKeyPoints)}
                className="flex items-center justify-between w-full text-left"
              >
                <span className="font-semibold flex items-center gap-2">
                  📌 Ключевые тезисы
                </span>
                <span className="text-[#626B73]">{showKeyPoints ? '▲' : '▼'}</span>
              </button>
              {showKeyPoints && (
                <ul className="mt-3 space-y-2">
                  {lesson.content.keyPoints.map((point, index) => (
                    <li key={index} className="flex items-start gap-2 text-[#3E454A]">
                      <span className="text-[#0F766E] mt-1">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

        {/* Code Examples */}
        {(lesson.content as any).outcomes && (lesson.content as any).outcomes.length > 0 && (
          <div className="mt-6 rounded-xl border border-[#E1E5E3] bg-white p-4">
            <h3 className="mb-2 font-semibold text-[#252A2D]">✅ Теперь вы умеете</h3>
            <ul className="space-y-1 text-[#252A2D]">
              {(lesson.content as any).outcomes.map((o: string, i: number) => (
                <li key={i} className="flex gap-2"><span className="text-[#0F766E]">•</span><span>{o}</span></li>
              ))}
            </ul>
          </div>
        )}
        {lesson.content.examples && lesson.content.examples.length > 0 && (
          <div className="mt-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              💻 Примеры кода
            </h3>
            <div className="space-y-4">
              {lesson.content.examples.map((example, index) => (
                <div key={index} className="bg-white border border-[#E1E5E3] rounded-xl overflow-hidden">
                  <div className="px-4 py-2 bg-[#F0F4F2] border-b border-[#E1E5E3] flex justify-between items-center">
                    <span className="text-sm text-[#3E454A]">{example.title}</span>
                    <span className="text-xs text-[#626B73]">{example.language}</span>
                  </div>
                  <SyntaxHighlighter
                    style={oneDark}
                    language={example.language}
                    customStyle={{ margin: 0, borderRadius: 0 }}
                  >
                    {example.code}
                  </SyntaxHighlighter>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Feedback */}
        <div className="mt-8 pt-6 border-t border-[#E1E5E3]">
          <div className="flex items-center justify-between">
            <span className="text-[#626B73] text-sm">Этот урок был полезен?</span>
            <div className="flex gap-2">
              <button
                onClick={() => handleFeedback('positive')}
                disabled={feedbackSent}
                className={`px-4 py-2 rounded-lg transition ${
                  feedbackSent
                    ? 'bg-[#E5F2EF] text-[#626B73] cursor-not-allowed'
                    : 'bg-white border border-[#E1E5E3] hover:bg-[#E5F2EF] hover:text-[#0F766E]'
                }`}
              >
                👍 Да
              </button>
              <button
                onClick={() => handleFeedback('negative')}
                disabled={feedbackSent}
                className={`px-4 py-2 rounded-lg transition ${
                  feedbackSent
                    ? 'bg-[#E5F2EF] text-[#626B73] cursor-not-allowed'
                    : 'bg-white border border-[#E1E5E3] hover:bg-[#FDECEC] hover:text-[#B91C1C]'
                }`}
              >
                👎 Нет
              </button>
            </div>
          </div>
          {feedbackSent && (
            <p className="text-[#0F766E] text-sm mt-2">Спасибо за отзыв!</p>
          )}
        </div>

        {/* Action Button */}
        {collectionId && moduleId && (
          <div className="mt-8">
            <button
              onClick={handleStartTest}
              className="w-full py-4 bg-[#0F766E] rounded-xl font-semibold text-lg hover:opacity-90 transition flex items-center justify-center gap-2"
            >
              📋 Пройти тест
              <span>→</span>
            </button>
          </div>
        )}
      </main>
    </div>
  );
}